/**
 * Pure sector-aware Profit & Loss benchmark evaluation.
 * Scoring lives here, not in React. Inputs are copied and never mutated.
 */

import {
    ARCHETYPE_THRESHOLDS as BS_ARCHETYPE_THRESHOLDS,
} from '../config/balanceSheetBenchmarkConfig';
import {
    ARCHETYPE_THRESHOLDS,
    METRIC_LABELS,
    NO_PAYOUT_NOTE,
    PROFIT_LOSS_NOT_APPLICABLE_REASONS,
    ROE_LEVERAGE_WARNING,
    ROIC_WACC_FLOOR_NOTE,
    SCORED_METRIC_IDS,
} from '../config/profitLossBenchmarkConfig';
import {
    PROFIT_LOSS_RATIOS_PANEL_MAP,
} from '../config/profitLossRatiosPanelBenchmarkMap';
import {
    BENCHMARK_SOURCE_HEURISTIC,
    FALLBACK_SECTOR_NOTE,
    METHODOLOGY_NOTE,
} from '../config/sectorTaxonomy';
import {
    applyThreshold,
    calculatePositiveYearsRatio,
    calculateRollingMedian,
    collectAnnualPoints,
    copyAligned,
    formatThresholdRule,
    isVolatile,
    median,
} from './benchmarkMath';
import {
    buildSectorBenchmarkProfile,
    isNotApplicableSector,
} from './sectorResolver';

export function getProfitLossBenchmarkProfile(rawSector) {
    return buildSectorBenchmarkProfile(rawSector, ARCHETYPE_THRESHOLDS, {
        notApplicableReasons: PROFIT_LOSS_NOT_APPLICABLE_REASONS,
    });
}

function metricLabel(metricId) {
    return METRIC_LABELS[metricId] || metricId;
}

function emptyEvaluation(overrides) {
    return {
        metricId: overrides.metricId,
        label: metricLabel(overrides.metricId),
        status: overrides.status,
        latestStatus: overrides.latestStatus || null,
        primaryValue: overrides.primaryValue ?? null,
        latestValue: overrides.latestValue ?? null,
        median3: overrides.median3 ?? null,
        median5: overrides.median5 ?? null,
        min: overrides.min ?? null,
        max: overrides.max ?? null,
        validAnnualCount: overrides.validAnnualCount || 0,
        positiveYearCount: overrides.positiveYearCount || 0,
        positiveYearRatio: overrides.positiveYearRatio ?? null,
        windowUsed: overrides.windowUsed || null,
        ruleText: overrides.ruleText || '',
        interpretation: overrides.interpretation || '',
        warnings: overrides.warnings || [],
        volatilityWarning: Boolean(overrides.volatilityWarning),
        source: BENCHMARK_SOURCE_HEURISTIC,
        unit: overrides.unit || null,
        notApplicableReason: overrides.notApplicableReason || null,
    };
}

function buildInterpretation(metricId, status, profile, extras = {}) {
    const sectorLabel = profile.sector.label;
    const label = metricLabel(metricId);
    if (status === 'notApplicable') {
        return extras.notApplicableReason || profile.notApplicableReason || `${label} is not applicable for this company type.`;
    }
    if (status === 'insufficientData') {
        return `Fewer than ${profile.requiredWindow} valid annual observations are available for a ${sectorLabel} sector score. Latest year is shown as context only.`;
    }
    if (metricId === 'roicWaccSpread' && status === 'weak' && extras.spreadFloor) {
        return ROIC_WACC_FLOOR_NOTE;
    }
    if (status === 'strong') return `Multi-year ${label} meets the initial ${sectorLabel} screening threshold.`;
    if (status === 'watch') return `Multi-year ${label} is below the strong ${sectorLabel} threshold and warrants explanation.`;
    return `Multi-year ${label} is materially below the ${sectorLabel} screening threshold and requires investigation.`;
}

/**
 * Intentional exception to the archetype-band model: destroying economic
 * value relative to the cost of capital is a universal red flag.
 */
function applyRoicWaccFloor(value, spec) {
    if (value != null && Number.isFinite(value) && value < 0) return 'weak';
    return applyThreshold(value, spec);
}

function isElevatedLeverage(value, spec) {
    if (value == null || !Number.isFinite(value) || !spec) return false;
    const status = applyThreshold(value, spec);
    if (status === 'weak') return true;
    if (status !== 'watch') return false;
    const t = spec.thresholds || {};
    if (spec.direction === 'band') {
        const mid = (t.strongMax + t.watchMax) / 2;
        return value >= mid;
    }
    if (spec.direction === 'lowerIsBetter') {
        const mid = (t.strong + t.watch) / 2;
        return value >= mid;
    }
    return true;
}

/**
 * Apply the same sector threshold to each valid annual observation.
 * A negative ROIC−WACC spread is always weak for that year.
 */
export function evaluatePerYearProfitLossStatus({ metricId, sector, values, years } = {}) {
    const profile = getProfitLossBenchmarkProfile(sector);
    const spec = metricId ? profile.metrics?.[metricId] : null;
    const copied = copyAligned(values, years);
    return collectAnnualPoints(copied.values, copied.years).map((point) => ({
        year: point.year,
        value: point.value,
        status: metricId === 'roicWaccSpread'
            ? applyRoicWaccFloor(point.value, spec)
            : applyThreshold(point.value, spec),
    }));
}

/**
 * Evaluate one normalized P&L metric against the sector heuristic.
 *
 * @param {object} params
 * @param {{ debtToEquityValues?: Array<number|null> }} [params.context]
 */
export function evaluateProfitLossMetric({
    metricId,
    sector,
    values,
    years,
    context = {},
} = {}) {
    const profile = getProfitLossBenchmarkProfile(sector);
    const copied = copyAligned(values, years);
    const warnings = [];
    if (profile.sector.unmapped) warnings.push(FALLBACK_SECTOR_NOTE);

    if (!metricId) {
        return emptyEvaluation({
            metricId: 'unknown',
            status: 'insufficientData',
            interpretation: 'No metric was supplied for evaluation.',
            warnings,
        });
    }

    if (isNotApplicableSector(profile.sector)) {
        return emptyEvaluation({
            metricId,
            status: 'notApplicable',
            notApplicableReason: profile.notApplicableReason,
            interpretation: profile.notApplicableReason,
            windowUsed: profile.primaryWindowLabel,
            warnings,
        });
    }

    const spec = profile.metrics?.[metricId];
    if (!spec) {
        return emptyEvaluation({
            metricId,
            status: 'notApplicable',
            notApplicableReason: `${metricLabel(metricId)} has no sector-aware P&L benchmark.`,
            interpretation: `${metricLabel(metricId)} has no sector-aware P&L benchmark.`,
            warnings,
        });
    }

    const ruleText = formatThresholdRule(metricId, spec, profile.requiredWindow, {
        label: metricLabel(metricId),
    });
    const annual = collectAnnualPoints(copied.values, copied.years);
    const annualValues = annual.map((point) => point.value);
    const latestPoint = annual.length ? annual[annual.length - 1] : null;
    const latestValue = latestPoint ? latestPoint.value : null;
    const median3 = annual.length >= 3 ? median(annual.slice(-3).map((point) => point.value)) : null;
    const median5 = annual.length >= 5 ? median(annual.slice(-5).map((point) => point.value)) : null;
    const primaryValue = profile.requiredWindow === 5 ? median5 : median3;
    const min = annualValues.length ? Math.min(...annualValues) : null;
    const max = annualValues.length ? Math.max(...annualValues) : null;
    const positiveYearCount = annualValues.filter((value) => value > 0).length;
    const positiveYearRatio = calculatePositiveYearsRatio(annualValues);
    const latestStatus = metricId === 'roicWaccSpread'
        ? applyRoicWaccFloor(latestValue, spec)
        : applyThreshold(latestValue, spec);

    if (metricId === 'dividendPayoutRatioScored') {
        const hasPayout = annual.some((point) => point.value != null && Number.isFinite(point.value) && point.value !== 0);
        if (!hasPayout) {
            return emptyEvaluation({
                metricId,
                status: 'notApplicable',
                notApplicableReason: NO_PAYOUT_NOTE,
                interpretation: NO_PAYOUT_NOTE,
                ruleText,
                windowUsed: profile.primaryWindowLabel,
                latestValue,
                validAnnualCount: annual.length,
                warnings,
                unit: spec.unit,
            });
        }
    }

    if (annual.length === 0) {
        const reason = metricId === 'grossProfitMargin'
            ? 'Screener does not report material cost or cost of goods sold for this company, so gross margin is not calculated.'
            : `${metricLabel(metricId)} has no reported annual values.`;
        return emptyEvaluation({
            metricId,
            status: 'notApplicable',
            notApplicableReason: reason,
            interpretation: reason,
            ruleText,
            windowUsed: profile.primaryWindowLabel,
            latestValue,
            validAnnualCount: 0,
            warnings,
            unit: spec.unit,
        });
    }

    if (annual.length < profile.requiredWindow || primaryValue == null) {
        return emptyEvaluation({
            metricId,
            status: 'insufficientData',
            latestStatus,
            latestValue,
            primaryValue: null,
            median3,
            median5,
            min,
            max,
            validAnnualCount: annual.length,
            positiveYearCount,
            positiveYearRatio,
            windowUsed: profile.primaryWindowLabel,
            ruleText,
            interpretation: buildInterpretation(metricId, 'insufficientData', profile),
            warnings: annual.length
                ? [...warnings, `Only ${annual.length} valid annual observation(s); ${profile.requiredWindow} are required for a sector score.`]
                : warnings,
            unit: spec.unit,
        });
    }

    let spreadFloor = false;
    let status;
    if (metricId === 'roicWaccSpread') {
        // Sector-independent floor: spread < 0 → weak, overriding archetype bands.
        if (primaryValue < 0) {
            status = 'weak';
            spreadFloor = true;
            warnings.push(ROIC_WACC_FLOOR_NOTE);
        } else {
            status = applyThreshold(primaryValue, spec) || 'watch';
        }
    } else {
        status = applyThreshold(primaryValue, spec) || 'watch';
    }

    const volatilityWarning = isVolatile(latestValue, primaryValue, spec);
    if (volatilityWarning) {
        warnings.push('Latest year diverges from the multi-year median; treat the latest reading as context, not as the sector score.');
    }

    if (metricId === 'returnOnEquity' && status === 'strong' && Array.isArray(context.debtToEquityValues)) {
        const deMedian = calculateRollingMedian(
            context.debtToEquityValues,
            copied.years.length ? copied.years : years,
            profile.requiredWindow,
        );
        const deSpec = (BS_ARCHETYPE_THRESHOLDS[profile.archetypeId] || {}).debtToEquity;
        if (isElevatedLeverage(deMedian, deSpec)) {
            warnings.push(ROE_LEVERAGE_WARNING);
        }
    }

    return {
        metricId,
        label: metricLabel(metricId),
        status,
        latestStatus,
        primaryValue,
        latestValue,
        median3,
        median5,
        min,
        max,
        validAnnualCount: annual.length,
        positiveYearCount,
        positiveYearRatio,
        windowUsed: profile.primaryWindowLabel,
        ruleText,
        interpretation: buildInterpretation(metricId, status, profile, { spreadFloor }),
        warnings,
        volatilityWarning,
        source: BENCHMARK_SOURCE_HEURISTIC,
        unit: spec.unit,
        notApplicableReason: null,
    };
}

function parseRatioNumber(raw) {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
    const cleaned = String(raw).replace(/[,\s₹]/g, '').replace(/%/g, '').replace(/[^\d.-]/g, '');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
}

export function seriesFromRatio(ratio) {
    const list = Array.isArray(ratio?.values) ? ratio.values : [];
    return {
        values: list.map((item) => parseRatioNumber(item?.value)),
        years: list.map((item) => String(item?.year ?? item?.displayYear ?? '')),
    };
}

function findRatio(ratios, name) {
    return (Array.isArray(ratios) ? ratios : []).find((ratio) => ratio && ratio.name === name) || null;
}

export function buildRoicWaccSpreadRatio(ratios) {
    const roic = findRatio(ratios, 'operatingRoic');
    const wacc = findRatio(ratios, 'wacc');
    if (!roic && !wacc) return null;
    const template = wacc || roic;
    const roicByYear = new Map();
    const waccByYear = new Map();
    (roic?.values || []).forEach((item) => {
        roicByYear.set(String(item.year ?? item.displayYear), parseRatioNumber(item?.value));
    });
    (wacc?.values || []).forEach((item) => {
        waccByYear.set(String(item.year ?? item.displayYear), parseRatioNumber(item?.value));
    });
    const years = (template.values || []).map((item) => item);
    const values = years.map((item) => {
        const year = String(item.year ?? item.displayYear);
        const a = roicByYear.get(year);
        const b = waccByYear.get(year);
        const spread = (a != null && b != null && Number.isFinite(a) && Number.isFinite(b))
            ? Number((a - b).toFixed(2))
            : null;
        return {
            ...item,
            value: spread,
            color: undefined,
        };
    });
    return {
        name: 'roicWaccSpread',
        displayName: 'ROIC minus WACC (Economic Spread)',
        unit: 'pp',
        description: 'Operating ROIC minus WACC; the economically relevant capital-efficiency spread.',
        formula: 'operatingRoic − wacc',
        values,
    };
}

export function injectRoicWaccSpreadRow(ratios) {
    const list = Array.isArray(ratios) ? ratios.slice() : [];
    if (list.some((ratio) => ratio?.name === 'roicWaccSpread')) return list;
    const derived = buildRoicWaccSpreadRatio(list);
    if (!derived) return list;
    const waccIndex = list.findIndex((ratio) => ratio?.name === 'wacc');
    const roicIndex = list.findIndex((ratio) => ratio?.name === 'operatingRoic');
    const insertAt = waccIndex >= 0 ? waccIndex + 1 : (roicIndex >= 0 ? roicIndex + 1 : list.length);
    list.splice(insertAt, 0, derived);
    return list;
}

function extractDebtToEquitySeries(balanceSheetRatios) {
    const row = findRatio(balanceSheetRatios, 'debtToEquity');
    return row ? seriesFromRatio(row) : { values: [], years: [] };
}

/**
 * Evaluate every mapped P&L Ratios table row against the shared resolver.
 */
export function evaluateProfitLossRatiosPanel({
    sector,
    ratios = [],
    balanceSheetRatios = [],
} = {}) {
    const profile = getProfitLossBenchmarkProfile(sector);
    const displayRatios = injectRoicWaccSpreadRow(ratios);
    const deSeries = extractDebtToEquitySeries(balanceSheetRatios);
    const context = { debtToEquityValues: deSeries.values };
    const byRatioName = {};

    Object.entries(PROFIT_LOSS_RATIOS_PANEL_MAP).forEach(([ratioName, mapping]) => {
        if (mapping.kind === 'notScored') {
            byRatioName[ratioName] = {
                kind: 'notScored',
                reason: mapping.reason,
                evaluation: null,
            };
            return;
        }
        const row = findRatio(displayRatios, ratioName);
        const series = row ? seriesFromRatio(row) : { values: [], years: [] };
        byRatioName[ratioName] = {
            kind: 'scored',
            metricId: mapping.metricId,
            evaluation: evaluateProfitLossMetric({
                metricId: mapping.metricId,
                sector,
                values: series.values,
                years: series.years.length ? series.years : deSeries.years,
                context,
            }),
        };
    });

    return {
        profile,
        byRatioName,
        displayRatios,
        methodologyNote: METHODOLOGY_NOTE,
        source: BENCHMARK_SOURCE_HEURISTIC,
        scoredMetricIds: SCORED_METRIC_IDS,
    };
}

export function evaluateProfitLossComparisonBenchmarks() {
    return null;
}
